// frontend/src/charts/Sparkline.tsx
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import type { ChartPoint } from "../api/charts.js";
import { METRICS, type MetricId } from "./METRICS.js";
import { chartTheme } from "./chart-theme.js";

export interface SparklineProps {
  metricId: MetricId;
  points: ChartPoint[];
  height?: number;
}

function formatDayShort(iso: string): string {
  const months = ["янв","фев","мар","апр","май","июн","июл","авг","сен","окт","ноя","дек"];
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${Number(m[3])} ${months[Number(m[2]) - 1] ?? ""}`;
}

export function Sparkline({ metricId, points, height = 36 }: SparklineProps) {
  if (points.length < 3) {
    return (
      <div style={{ color: "var(--text-muted)", fontSize: 12, height }}>
        недостаточно истории
      </div>
    );
  }
  const meta = METRICS[metricId];
  const color = meta.seriesColor;
  const gradId = `sparkline-grad-${metricId}`;
  const data = points.map((p) => ({
    day: p.day,
    value: (p as unknown as Record<string, number | null>)[metricId] ?? null,
  }));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={chartTheme.areaGradientFromOpacity} />
            <stop offset="100%" stopColor={color} stopOpacity={chartTheme.areaGradientToOpacity} />
          </linearGradient>
        </defs>
        <Tooltip
          contentStyle={{
            background: "var(--surface-card, #fff)",
            border: "1px solid var(--border-default, #E2E8F0)",
            borderRadius: 6,
            fontSize: 11,
            padding: "4px 8px",
            fontFamily: "inherit",
          }}
          labelFormatter={(label) => formatDayShort(label as string)}
          formatter={(value: unknown) => [meta.format(value as number), meta.label]}
          cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: "3 3" }}
          isAnimationActive={false}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={chartTheme.lineStrokeWidth}
          fill={`url(#${gradId})`}
          dot={false}
          activeDot={{ r: 3, stroke: color, strokeWidth: 1.5, fill: "var(--surface-card, #fff)" }}
          connectNulls={false}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
